import React, { useState, useEffect } from "react";
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../contexts/LanguageContext";
import type { Language } from "../types/lang.types";

const languageLabels: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
  ar: "العربية",
};

const SCROLL_THRESHOLD = 60;

const PublicLayout: React.FC = () => {
  const { currentLanguage, changeLanguage, languages, t } = useLanguage();
  const { pathname } = useLocation();
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isHome = pathname === "/" || pathname === "/kooperation" || pathname.startsWith("/ueber-uns");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close drawer + scroll to top on route change
  useEffect(() => {
    setDrawerOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    changeLanguage(lang);
    handleLangMenuClose();
    setDrawerOpen(false);
  };

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/kooperation", label: t("nav.kooperation") },
    { to: "/preise", label: t("nav.preise") },
    { to: "/ueber-uns", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  // Navbar: pure white left → ice blue → vivid blue → medium blue right
  const navGradient =
    "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(220,235,255,0.97) 10%, rgba(140,185,245,0.94) 28%, rgba(60,120,230,0.94) 55%, rgba(32,96,210,0.95) 78%, rgba(22,80,200,0.96) 100%)";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundImage: scrolled || !isHome ? navGradient : "none",
          backgroundColor: "transparent",
          backdropFilter:
            scrolled || !isHome ? "blur(20px) saturate(1.4)" : "none",
          borderBottom:
            scrolled || !isHome ? "1px solid rgba(255,255,255,0.18)" : "none",
          boxShadow:
            scrolled || !isHome
              ? "0 1px 0 rgba(255,255,255,0.12) inset, 0 6px 40px rgba(6,20,90,0.32)"
              : "none",
          transition:
            "background-image 0.45s ease, backdrop-filter 0.45s ease, box-shadow 0.45s ease, border-color 0.45s ease",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{ justifyContent: "space-between", py: 1, minHeight: 64 }}
          >
            {/* Logo */}
            <RouterLink to="/">
              <Box
                component="img"
                src="https://top-bhs.de/site/img/logo-footer-black.png"
                alt="TOP BHS"
                sx={{
                  height: { xs: 80, md: 130 },
                  display: "block",
                }}
              />
            </RouterLink>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 0.5,
                alignItems: "center",
              }}
            >
              {navLinks.slice(0, 5).map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    transition: "color 0.35s ease",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.1)",
                      textDecoration: "none",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}

              <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                sx={{
                  ml: 1.5,
                  borderColor: "rgba(255,255,255,0.6)",
                  borderWidth: "1.5px",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    borderWidth: "1.5px",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                  transition: "all 0.35s ease",
                }}
              >
                {t("nav.contact")}
              </Button>

              {/* Language Switcher */}
              <IconButton
                onClick={handleLangMenuOpen}
                sx={{
                  ml: 1,
                  color: "rgba(255,255,255,0.7)",
                  transition: "color 0.35s ease",
                  "&:hover": { color: "white" },
                }}
                size="small"
                aria-label="Sprache wechseln"
                aria-haspopup="true"
              >
                <LanguageIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      border: "1px solid #e8e8e8",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    selected={lang === currentLanguage}
                    sx={{ fontSize: "0.9rem", minWidth: 120 }}
                  >
                    {languageLabels[lang]}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Mobile: Language + Hamburger */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <IconButton
                onClick={handleLangMenuOpen}
                sx={{ color: "rgba(255,255,255,0.8)" }}
                size="small"
                aria-label="Sprache wechseln"
                aria-haspopup="true"
              >
                <LanguageIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      border: "1px solid #e8e8e8",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    selected={lang === currentLanguage}
                    sx={{ fontSize: "0.9rem", minWidth: 120 }}
                  >
                    {languageLabels[lang]}
                  </MenuItem>
                ))}
              </Menu>

              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: "white" }}
                size="medium"
                aria-label="Menü öffnen"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundImage:
              "linear-gradient(to bottom, #c8e4ff 0%, #5a90e0 30%, #1a3ea8 70%, #060f40 100%)",
            color: "white",
            boxShadow: "-8px 0 40px rgba(8,26,103,0.4)",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <RouterLink to="/" onClick={() => setDrawerOpen(false)}>
            <Box
              component="img"
              src="https://top-bhs.de/site/img/logo-footer-black.png"
              alt="TOP BHS"
              sx={{ height: 90, display: "block" }}
            />
          </RouterLink>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "rgba(255,255,255,0.7)" }}
            aria-label="Menü schließen"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Nav Links */}
        <List sx={{ pt: 2, px: 1 }}>
          {navLinks.map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  color: pathname === item.to ? "white" : "white",
                  bgcolor:
                    pathname === item.to
                      ? "rgba(255,255,255,0.12)"
                      : "transparent",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "white",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: pathname === item.to ? 600 : 400,
                    fontSize: "1rem",
                    color: "white",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2, my: 2 }} />

        {/* Language selection in drawer */}
        <Box sx={{ px: 3 }}>
          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.65rem",
              letterSpacing: "2px",
              display: "block",
              mb: 1.5,
            }}
          >
            Sprache
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {languages.map((lang) => (
              <Button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                size="small"
                variant={lang === currentLanguage ? "contained" : "outlined"}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color:
                    lang === currentLanguage
                      ? "#525b6d"
                      : "rgba(255,255,255,0.7)",
                  bgcolor: lang === currentLanguage ? "white" : "transparent",
                  fontSize: "0.78rem",
                  "&:hover": {
                    bgcolor:
                      lang === currentLanguage
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.6)",
                  },
                }}
              >
                {languageLabels[lang]}
              </Button>
            ))}
          </Box>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, pt: isHome ? 0 : "64px" }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(220,235,255,0.97) 10%, rgba(140,185,245,0.94) 28%, rgba(60,120,230,0.94) 55%, rgba(32,96,210,0.95) 78%, rgba(22,80,200,0.96) 100%)",
          color: "white",
          pt: { xs: 8, md: 12 },
          pb: 4,
          borderTop: "1px solid rgba(160,210,255,0.25)",
        }}
      >
        {/* ── Top shimmer accent line ── */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(to right, rgba(255,255,255,0.85) 0%, rgba(160,220,255,0.6) 25%, rgba(80,150,240,0.25) 55%, transparent 100%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        {/* ── Wavy colour bands + plexus SVG ── */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1440 700"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              {/* Wave fill gradients — bright white-blue left, fade out right */}
              <linearGradient id="waveA" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
                <stop offset="30%" stopColor="#7ec4f8" stopOpacity="0.30" />
                <stop offset="72%" stopColor="#2060c0" stopOpacity="0.10" />
                <stop offset="100%" stopColor="#0a2a80" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="waveB" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#e0f2ff" stopOpacity="0.28" />
                <stop offset="40%" stopColor="#50a8f0" stopOpacity="0.20" />
                <stop offset="70%" stopColor="#1a60c8" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#0a2a80" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="waveC" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.32" />
                <stop offset="25%" stopColor="#a8d8ff" stopOpacity="0.28" />
                <stop offset="58%" stopColor="#3878d8" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#0a2a80" stopOpacity="0" />
              </linearGradient>
              <filter
                id="node-glow"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* ── Wave band A — largest, outermost soft blue wash ── */}
            <path
              d="M 0 0
                 C 180 30  340 -20 500 60
                 C 660 140 720  80 860 100
                 C 1000 120 1080 60 1200 80
                 C 1320 100 1400 40 1440 50
                 L 1440 700 L 0 700 Z"
              fill="url(#waveA)"
            />

            {/* ── Wave band B — medium, tighter undulation ── */}
            <path
              d="M 0 0
                 C 120 50  240 -10 380 70
                 C 520 150 600  90 720 110
                 C 840 130 900  70 1020 85
                 C 1140 100 1220 50 1300 65
                 L 1300 700 L 0 700 Z"
              fill="url(#waveB)"
            />

            {/* ── Wave band C — smallest, crispest left-edge highlight ── */}
            <path
              d="M 0 0
                 C 80 60  180 10  280 80
                 C 380 150 440 100 540 115
                 C 640 130 700  85  800  95
                 C 900 105 960  70 1040  80
                 L 1040 700 L 0 700 Z"
              fill="url(#waveC)"
            />

            {/* ── Plexus — connection lines (stronger on right/dark side) ── */}
            <g stroke="rgba(110,185,255,0.22)" strokeWidth="0.7" fill="none">
              <line x1="320" y1="180" x2="480" y2="240" />
              <line x1="320" y1="180" x2="700" y2="300" />
              <line x1="520" y1="60" x2="640" y2="140" />
              <line x1="640" y1="140" x2="480" y2="240" />
              <line x1="640" y1="140" x2="700" y2="300" />
              <line x1="640" y1="140" x2="850" y2="80" />
              <line x1="640" y1="140" x2="960" y2="180" />
              <line x1="480" y1="240" x2="700" y2="300" />
              <line x1="700" y1="300" x2="850" y2="80" />
              <line x1="700" y1="300" x2="920" y2="450" />
              <line x1="700" y1="300" x2="750" y2="500" />
              <line x1="850" y1="80" x2="960" y2="180" />
              <line x1="850" y1="80" x2="1080" y2="90" />
              <line x1="960" y1="180" x2="1080" y2="90" />
              <line x1="960" y1="180" x2="1100" y2="380" />
              <line x1="960" y1="180" x2="920" y2="450" />
              <line x1="1080" y1="90" x2="1200" y2="150" />
              <line x1="1080" y1="90" x2="1380" y2="220" />
              <line x1="1080" y1="90" x2="1280" y2="320" />
              <line x1="1200" y1="150" x2="1350" y2="80" />
              <line x1="1200" y1="150" x2="1380" y2="220" />
              <line x1="1350" y1="80" x2="1380" y2="220" />
              <line x1="1380" y1="220" x2="1280" y2="320" />
              <line x1="1380" y1="220" x2="1100" y2="380" />
              <line x1="1280" y1="320" x2="1100" y2="380" />
              <line x1="1280" y1="320" x2="920" y2="450" />
              <line x1="1100" y1="380" x2="920" y2="450" />
              <line x1="1100" y1="380" x2="750" y2="500" />
              <line x1="920" y1="450" x2="750" y2="500" />
              <line x1="920" y1="450" x2="580" y2="440" />
              <line x1="750" y1="500" x2="580" y2="440" />
              <line x1="580" y1="440" x2="400" y2="480" />
            </g>

            {/* Regular plexus nodes */}
            <g fill="rgba(150,210,255,0.45)">
              {[
                [480, 240],
                [640, 140],
                [700, 300],
                [850, 80],
                [960, 180],
                [1080, 90],
                [1200, 150],
                [1350, 80],
                [1380, 220],
                [1280, 320],
                [1100, 380],
                [920, 450],
                [750, 500],
                [580, 440],
                [400, 480],
              ].map(([cx, cy], i) => (
                <circle key={i} cx={cx} cy={cy} r="2" />
              ))}
            </g>

            {/* Glowing nodes on right/dark side */}
            <g filter="url(#node-glow)">
              <circle cx="1350" cy="80" r="5" fill="rgba(80,165,255,0.95)" />
              <circle cx="960" cy="180" r="4.5" fill="rgba(100,185,255,0.85)" />
              <circle cx="1100" cy="380" r="4" fill="rgba(90,175,255,0.80)" />
            </g>
          </svg>
        </Box>

        {/* ── Subtle corner glow (bottom-right depth) ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: -120,
            right: -80,
            width: 480,
            height: 480,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(14,55,140,0.30) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Main grid: logo + 3 link columns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "2fr 1fr 1fr 1fr",
              },
              gap: { xs: 6, md: 8 },
              mb: { xs: 8, md: 12 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <RouterLink to="/">
                <Box
                  component="img"
                  src="https://top-bhs.de/site/img/logo-footer-black.png"
                  alt="TOP BHS"
                  sx={{ height: { xs: 100, md: 300 }, display: "block", mb: 4 }}
                />
              </RouterLink>
            </Box>

            {/* Leistungen */}
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(160,210,255,0.7)",
                  letterSpacing: "2.5px",
                  fontSize: "0.65rem",
                  mb: 3,
                  display: "block",
                }}
              >
                {t("footer.services_heading")}
              </Typography>
              {[
                {
                  label: t("footer.service_finanzbuchhaltung"),
                  to: "/services",
                },
                { label: t("footer.service_lohnbuchhaltung"), to: "/services" },
                {
                  label: t("footer.service_unternehmensberatung"),
                  to: "/services",
                },
                { label: t("nav.preise"), to: "/preise" },
              ].map((item) => (
                <RouterLink
                  key={item.label}
                  to={item.to}
                  style={{
                    display: "block",
                    color: "rgba(255,255,255,0.65)",
                    marginBottom: "13px",
                    textDecoration: "none",
                    fontSize: "0.88rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.65)")
                  }
                >
                  {item.label}
                </RouterLink>
              ))}
            </Box>

            {/* Unternehmen */}
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(160,210,255,0.7)",
                  letterSpacing: "2.5px",
                  fontSize: "0.65rem",
                  mb: 3,
                  display: "block",
                }}
              >
                {t("footer.company_heading")}
              </Typography>
              {[
                { label: t("footer.about"), to: "/ueber-uns" },
                { label: t("nav.kooperation"), to: "/kooperation" },
                { label: t("nav.preise"), to: "/preise" },
                { label: t("nav.contact"), to: "/contact" },
              ].map((item) => (
                <RouterLink
                  key={item.label}
                  to={item.to}
                  style={{
                    display: "block",
                    color: "rgba(255,255,255,0.65)",
                    marginBottom: "13px",
                    textDecoration: "none",
                    fontSize: "0.88rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.65)")
                  }
                >
                  {item.label}
                </RouterLink>
              ))}
            </Box>

            {/* Rechtliches + Social */}
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(160,210,255,0.7)",
                  letterSpacing: "2.5px",
                  fontSize: "0.65rem",
                  mb: 3,
                  display: "block",
                }}
              >
                {t("footer.legal_heading")}
              </Typography>
              {[
                { label: t("footer.privacy"), to: "/datenschutz" },
                { label: t("footer.imprint"), to: "/impressum" },
                { label: t("footer.terms"), to: "/agb" },
              ].map((item) => (
                <RouterLink
                  key={item.to}
                  to={item.to}
                  style={{
                    display: "block",
                    color: "rgba(255,255,255,0.65)",
                    marginBottom: "13px",
                    textDecoration: "none",
                    fontSize: "0.88rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,0.65)")
                  }
                >
                  {item.label}
                </RouterLink>
              ))}

              <Typography
                variant="overline"
                sx={{
                  color: "rgba(160,210,255,0.7)",
                  letterSpacing: "2.5px",
                  fontSize: "0.65rem",
                  mb: 3,
                  mt: 5,
                  display: "block",
                }}
              >
                {t("footer.social_heading")}
              </Typography>
              {["LinkedIn", "Instagram", "X"].map((s) => (
                <Typography
                  key={s}
                  variant="body2"
                  sx={{
                    display: "block",
                    color: "rgba(255,255,255,0.65)",
                    mb: 1.5,
                    fontSize: "0.88rem",
                    cursor: "pointer",
                    "&:hover": { color: "white" },
                    transition: "color 0.2s",
                  }}
                >
                  {s}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.08)",
              pt: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}
            >
              © 2026 TOP Buchhaltungsservice UG. {t("footer.rights")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}
            >
              Empowered by{" "}
              <Box
                component="a"
                href="https://sma-it.de"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "rgba(160,210,255,0.75)",
                  fontWeight: 500,
                  textDecoration: "none",
                  "&:hover": { color: "white" },
                  transition: "color 0.2s",
                }}
              >
                SMA nexus
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
